import React, { useState } from "react";
import { useTranslation } from "react-i18next";
import { api } from "@/api";
import { useSettings } from "@/contexts/SettingsContext";
import { Section, Overline } from "@/components/Primitives";
import Amenities from "@/components/Amenities";
import Payments from "@/components/Payments";
import { Phone, MessageCircle, MapPin, CalendarCheck, Send, Check } from "lucide-react";
import { Link } from "react-router-dom";

export default function Contact() {
  const { t, i18n } = useTranslation();
  const { settings: s } = useSettings();
  const [form, setForm] = useState({ name: "", email: "", phone: "", subject: "", message: "" });
  const [status, setStatus] = useState({ loading: false, ok: false });

  const submit = async (e) => {
    e.preventDefault();
    setStatus({ loading: true, ok: false });
    try {
      await api.post("/contact", { ...form, language: i18n.language });
      setStatus({ loading: false, ok: true });
      setForm({ name: "", email: "", phone: "", subject: "", message: "" });
    } catch { setStatus({ loading: false, ok: false }); }
  };
  const upd = (k, v) => setForm(f => ({ ...f, [k]: v }));

  const phoneClean = s?.phone?.replace(/\s/g, "");

  return (
    <div data-testid="contact-page" className="pt-32">
      <Section testId="contact-header">
        <Overline>{t("contact_page.overline")}</Overline>
        <h1 className="font-serif-luxe text-5xl sm:text-6xl font-light tracking-tight text-[#F8F5EF] mt-3 mb-6">{t("contact_page.title")}</h1>
        <p className="max-w-3xl text-[#F8F5EF]/70 leading-relaxed">{t("contact_page.intro")}</p>
      </Section>

      <Section testId="contact-actions" className="!pt-0">
        <div className="overline mb-4">{t("contact_page.quick_actions")}</div>
        <div className="grid grid-cols-2 md:grid-cols-4 gap-3">
          {s && (
            <>
              <a data-testid="contact-quick-call" href={`tel:${phoneClean}`} className="luxe-card p-6 flex flex-col items-center gap-2 hover:border-[#D4AF37]/50"><Phone size={20} className="text-[#D4AF37]" /><span className="text-xs tracking-[0.2em] uppercase">{t("contact_page.call")}</span></a>
              <a data-testid="contact-quick-whatsapp" href={`https://wa.me/${s.whatsapp_raw}`} target="_blank" rel="noreferrer" className="luxe-card p-6 flex flex-col items-center gap-2 hover:border-[#D4AF37]/50"><MessageCircle size={20} className="text-[#D4AF37]" /><span className="text-xs tracking-[0.2em] uppercase">{t("contact_page.whatsapp")}</span></a>
              <a data-testid="contact-quick-directions" href={s.maps_url} target="_blank" rel="noreferrer" className="luxe-card p-6 flex flex-col items-center gap-2 hover:border-[#D4AF37]/50"><MapPin size={20} className="text-[#D4AF37]" /><span className="text-xs tracking-[0.2em] uppercase">{t("contact_page.directions")}</span></a>
              <Link data-testid="contact-quick-reserve" to="/reservation" className="luxe-card p-6 flex flex-col items-center gap-2 hover:border-[#D4AF37]/50"><CalendarCheck size={20} className="text-[#D4AF37]" /><span className="text-xs tracking-[0.2em] uppercase">{t("contact_page.reserve")}</span></Link>
            </>
          )}
        </div>
      </Section>

      <Section testId="contact-form" className="!pt-0">
        <div className="grid grid-cols-1 lg:grid-cols-12 gap-10">
          <form onSubmit={submit} data-testid="contact-form-block" className="lg:col-span-7 luxe-card p-8 lg:p-10 space-y-5">
            <div className="grid grid-cols-1 sm:grid-cols-2 gap-5">
              <div><label className="overline mb-2 block">{t("contact_page.name")}</label><input data-testid="ct-name" required value={form.name} onChange={e=>upd("name",e.target.value)} className="input-luxe" /></div>
              <div><label className="overline mb-2 block">{t("contact_page.email")}</label><input data-testid="ct-email" required type="email" value={form.email} onChange={e=>upd("email",e.target.value)} className="input-luxe" /></div>
              <div><label className="overline mb-2 block">{t("contact_page.phone")}</label><input data-testid="ct-phone" value={form.phone} onChange={e=>upd("phone",e.target.value)} className="input-luxe" /></div>
              <div><label className="overline mb-2 block">{t("contact_page.subject")}</label><input data-testid="ct-subject" value={form.subject} onChange={e=>upd("subject",e.target.value)} className="input-luxe" /></div>
              <div className="sm:col-span-2"><label className="overline mb-2 block">{t("contact_page.message")}</label><textarea data-testid="ct-message" required rows={6} value={form.message} onChange={e=>upd("message",e.target.value)} className="input-luxe" /></div>
            </div>
            <button data-testid="ct-submit" type="submit" disabled={status.loading} className="btn-gold disabled:opacity-50">{t("contact_page.submit")} <Send size={14} /></button>
            {status.ok && <div data-testid="ct-success" className="flex items-center gap-2 text-emerald-400 text-sm"><Check size={16} /> {t("contact_page.success")}</div>}
          </form>

          <aside className="lg:col-span-5 space-y-4">
            {s?.maps_embed && (
              <iframe title="map" src={s.maps_embed} className="w-full h-64 border border-[#D4AF37]/20 grayscale contrast-125" />
            )}
            <div className="luxe-card p-6 space-y-3 text-sm">
              <div><div className="overline">{t("footer.address_label")}</div><div className="text-[#F8F5EF]/80 mt-1">{s?.address}</div></div>
              <div><div className="overline">{t("footer.phone_label")}</div><div className="text-[#F8F5EF]/80 mt-1">{s?.phone}</div></div>
              <div><div className="overline">{t("footer.email_label")}</div><div className="text-[#F8F5EF]/80 mt-1">{s?.email}</div></div>
              <div><div className="overline">{t("footer.hours_label")}</div><div className="text-[#F8F5EF]/80 mt-1 whitespace-pre-line">{s?.hours?.[i18n.language] || s?.hours?.fr}</div></div>
            </div>
          </aside>
        </div>
      </Section>

      <Section testId="contact-amenities" className="!pt-0">
        <Amenities />
        <Payments />
      </Section>
    </div>
  );
}
